import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ITicket } from '@/interfaces/tickets/ITicket';
import { ticketsService } from '@/services/tickets.service';
import { notificationService } from '@/services/notification.service';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from '@/components/common/Modal';
import { useAuthProvider } from '@/pages/auth/hooks/useAuthProvider';

const SCROLL_POSITION_KEY = 'ticketListScrollPosition';

export const TicketList = () => {
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [ticketToView, setTicketToView] = useState<ITicket | null>(null);
  const [ticketImageUrl, setTicketImageUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const { isAuthenticated } = useAuth();
  const { handleRefreshSession } = useAuthProvider();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRestoredScroll = useRef(false);

  // Restore scroll position after tickets are loaded
  useEffect(() => {
    if (!isLoading && tickets.length > 0 && !hasRestoredScroll.current) {
      const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (savedPosition) {
        // Small delay to ensure DOM is rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
        }, 100);
      }
      hasRestoredScroll.current = true;
    }
  }, [isLoading, tickets]);

  // Save scroll position before navigating away
  const saveScrollPosition = () => {
    sessionStorage.setItem(SCROLL_POSITION_KEY, window.scrollY.toString());
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadTickets();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handleSubscriptionRedirect = async () => {
      const subscription = searchParams.get('subscription');
      if (subscription === 'success') {
        try {
          await handleRefreshSession();
          notificationService.success('Subscription activated successfully!');
          loadTickets();
        } catch (error) {
          notificationService.error('Error updating subscription status');
        }
        navigate('/tickets', { replace: true });
      } else if (subscription === 'cancelled') {
        notificationService.error('Subscription cancelled');
        navigate('/tickets', { replace: true });
      }
    };

    handleSubscriptionRedirect();
  }, [searchParams, navigate, handleRefreshSession]);

  const loadTickets = async () => {
    try {
      const response = await ticketsService.getMyTickets();
      const ticketList = response.data.map(({ attributes }) => ({
        ...attributes,
        uuid: attributes.uuid
      }));
      setTickets(ticketList);
    } catch (error) {
      if (error instanceof Error) {
        notificationService.error(error.message);
      } else {
        notificationService.error('Error loading tickets');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTicket = async (ticket: ITicket) => {
    setTicketToView(ticket);
    setShowViewModal(true);
    setTicketImageUrl(null);
    
    // Only fetch image if ticket has a file
    if (ticket.file) {
      setIsLoadingImage(true);
      try {
        const blob = await ticketsService.downloadTicket(ticket.uuid);
        const imageUrl = URL.createObjectURL(blob);
        setTicketImageUrl(imageUrl);
      } catch (error) {
        console.error('Error loading ticket image:', error);
      } finally {
        setIsLoadingImage(false);
      }
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setTicketToView(null);
    // Clean up the object URL to prevent memory leaks
    if (ticketImageUrl) {
      URL.revokeObjectURL(ticketImageUrl);
      setTicketImageUrl(null);
    }
  };

  const handleDownloadTicket = async (ticket: ITicket) => {
    try {
      const blob = await ticketsService.downloadTicket(ticket.uuid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.eventName}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      notificationService.error('Error downloading ticket');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="loading-message">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F9015]"></div>
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={showViewModal}
        onClose={closeViewModal}
        title={ticketToView?.eventName || "Ticket Preview"}
      >
        {ticketToView && (
          <div className="p-4" data-testid="ticket-preview-modal">
            {/* Ticket Image Display */}
            <div className="mb-4">
              {!ticketToView.file ? (
                <div className="flex flex-col items-center justify-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-200">
                  <svg className="w-16 h-16 text-amber-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-lg font-semibold text-amber-800">Ticket Not Yet Released</span>
                  <p className="text-sm text-amber-600 mt-1">The ticket file will be available soon</p>
                </div>
              ) : isLoadingImage ? (
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F9015] mb-4"></div>
                  <span className="text-gray-600">Loading ticket...</span>
                </div>
              ) : ticketImageUrl ? (
                <div className="flex justify-center bg-gray-100 rounded-lg p-2">
                  <img 
                    src={ticketImageUrl} 
                    alt={`Ticket for ${ticketToView.eventName}`}
                    className="max-w-full max-h-[60vh] object-contain rounded shadow-lg"
                    data-testid="ticket-preview-image"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-red-50 rounded-lg border-2 border-dashed border-red-200">
                  <svg className="w-16 h-16 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-lg font-semibold text-red-800">Failed to load ticket</span>
                  <p className="text-sm text-red-600 mt-1">Please try again or view full details</p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                <span className="font-semibold">${ticketToView.price}</span> · {ticketToView.payment_status}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={closeViewModal}
                  data-testid="close-preview-button"
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Close
                </button>
                <Link
                  to={`/tickets/${ticketToView.uuid}`}
                  onClick={saveScrollPosition}
                  data-testid="full-details-button"
                  className="px-4 py-2 bg-[#0F9015] text-white rounded-lg hover:opacity-90 font-semibold"
                >
                  Full Details
                </Link>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#0F9015]" data-testid="page-title">My Tickets</h1>
        </div>

        {tickets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xl text-[#084509] font-medium" data-testid="no-tickets-message">No tickets available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tickets-grid">
            {tickets.map((ticket) => (
              <div
                key={ticket.uuid}
                data-testid={`ticket-card-${ticket.uuid}`}
                className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow flex flex-col h-[280px]"
              >
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-[#0F9015]" data-testid={`ticket-title-${ticket.uuid}`}>{ticket.eventName}</h2>
                  {!ticket.file && (
                    <span 
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                      data-testid={`ticket-unreleased-${ticket.uuid}`}
                    >
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      Unreleased
                    </span>
                  )}
                </div>
                <div className="space-y-3 flex-grow">
                  <p className="text-lg font-semibold text-[#084509]" data-testid={`ticket-price-${ticket.uuid}`}>
                    Price: <span className="font-bold">${ticket.price}</span>
                  </p>
                  <p className="text-lg font-semibold text-[#084509]" data-testid={`ticket-status-${ticket.uuid}`}>
                    Status: <span className="font-bold">{ticket.payment_status}</span>
                  </p>
                  {ticket.email && (
                    <p className="text-lg font-semibold text-[#084509]" data-testid={`ticket-email-${ticket.uuid}`}>
                      To: <span className="font-bold">{ticket.email}</span>
                    </p>
                  )}
                </div>
                
                <div className="flex justify-between items-center">
                  <Link
                    to={`/tickets/${ticket.uuid}`}
                    onClick={saveScrollPosition}
                    data-testid={`view-details-${ticket.uuid}`}
                    className="text-lg font-semibold text-[#0F9015] hover:text-[#084509] transition-colors"
                  >
                    View Details
                  </Link>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewTicket(ticket)}
                      data-testid={`view-ticket-${ticket.uuid}`}
                      className="px-4 py-2 bg-[#C9FD05] text-black font-semibold rounded-lg hover:bg-[#b4e404] transition-colors shadow-md flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                    {ticket.file && (
                      <button
                        onClick={() => handleDownloadTicket(ticket)}
                        data-testid={`download-ticket-${ticket.uuid}`}
                        className="p-2 bg-[#0F9015] text-white rounded-lg hover:bg-[#084509] transition-colors shadow-md"
                        title="Download Ticket"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}; 